/* eslint @typescript-eslint/strict-boolean-expressions: off */

import { gql } from "@apollo/client";

import { queryItemGqlFragment } from "src/components/page-components/BrowseQueriesPage/api/queryItem";

import { ListMyQueriesQueryVariables, Order_By } from "src/gql/codegen/graphql";

import type { SessionWithUser } from "src/utils/users/types";
import { Sort } from "src/components/page-components/Library/useOrderState";

/* Get variables for the query */

export interface ApiParams {
  filter: {
    freetext: string;
    owner?: {
      type: "user" | "team";
      handle: string;
    };
    publicness: "all" | "public" | "private";
    is_archived: boolean;
    is_matview: boolean;
  };
  order: Sort;
  page: number;
  pageSize: number;
}

export function getListMyQueriesVariables(
  input: ApiParams,
  session: SessionWithUser
): ListMyQueriesQueryVariables {
  function order() {
    switch (input.order.by) {
      case "favorites": {
        const favorite_view =
          input.order.time_range === "all"
            ? "query_favorite_count_all"
            : `query_favorite_count_last_${input.order.time_range}`;
        return [
          { [favorite_view]: { favorite_count: Order_By.DescNullsLast } },
          { id: Order_By.Asc },
        ];
      }
      case "created_at": {
        return { created_at: Order_By[input.order.order] };
      }

      case "updated_at": {
        return { updated_at: Order_By[input.order.order] };
      }
    }
  }

  function includeFavs() {
    function isFavs(time_range: string): boolean {
      return (
        input.order.by === "favorites" && input.order.time_range === time_range
      );
    }
    return {
      include_favs_last_24h: isFavs("24h"),
      include_favs_last_7d: isFavs("7d"),
      include_favs_last_30d: isFavs("30d"),
      include_favs_all_time: isFavs("all") || input.order.by !== "favorites",
    };
  }

  function filterIsPrivate() {
    switch (input.filter.publicness) {
      case "all":
        return {};
      case "public":
        return { _eq: false };
      case "private":
        return { _eq: true };
    }
  }

  function filterOwner() {
    const { owner } = input.filter;
    if (!owner) {
      return {
        _or: [
          { user_id: { _eq: session.user.id } },
          { team: { memberships: { user_id: { _eq: session.user.id } } } },
        ],
      };
    }
    switch (owner.type) {
      case "team":
        return [{ team: { handle: { _eq: owner.handle } } }];
      case "user":
        return [{ user: { name: { _eq: owner.handle } } }];
    }
  }

  return {
    filter_name: input.filter.freetext
      ? { _ilike: `%${input.filter.freetext}%` }
      : {},
    filter_is_private: filterIsPrivate(),
    filter_custom: filterOwner(),
    is_archived: input.filter.is_archived ?? false,
    filter_is_matview: input.filter.is_matview ? { _is_null: false } : {},
    order: order(),
    limit: input.pageSize,
    offset: input.pageSize * (input.page - 1),

    ...includeFavs(),
  };
}

/* The actual query */
// Not assigned to constant here. Using generated. See _GrahphQL requests_ in readme.
gql`
  query ListMyQueries(
    $filter_name: String_comparison_exp
    $filter_is_private: Boolean_comparison_exp
    $filter_is_matview: String_comparison_exp
    $filter_custom: [queries_bool_exp!]
    $is_archived: Boolean!
    $order: [queries_order_by!]
    $limit: Int!
    $offset: Int!
    $include_favs_last_24h: Boolean! = false
    $include_favs_last_7d: Boolean! = false
    $include_favs_last_30d: Boolean! = false
    $include_favs_all_time: Boolean! = false
  ) {
    queries(
      where: {
        is_temp: { _eq: false }
        is_archived: { _eq: $is_archived }
        name: $filter_name
        is_private: $filter_is_private
        matview_id: $filter_is_matview
        _and: $filter_custom
      }
      limit: $limit
      offset: $offset
      order_by: $order
    ) {
      ...QueryItem
    }
    queries_aggregate(
      where: {
        is_temp: { _eq: false }
        is_archived: { _eq: $is_archived }
        name: $filter_name
        matview_id: $filter_is_matview
        is_private: $filter_is_private
        _and: $filter_custom
      }
    ) {
      aggregate {
        count
      }
    }
  }

  ${queryItemGqlFragment}
`;
